import {AgendaelementModell, TilgangsrolleMedlem} from "../tjenester/ressurser/generertNg2HttpKlient";
import {GjeldendeTidssone, Tidssoneinformasjon} from "../tjenester/i18n/GjeldendeTidssone";
import {erIkkeNil, erNil} from "../støttefunksjoner/nilsjekk";
import {nil} from "felles-ng2/typer/nullish";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {lesMøtedato, MøteMedDato} from "felles-ng2/modell/møteModellHjelpere";
import {durationFraMinutter, lesZonedDateTimeSerialisertOpt, plainTimeFraZonedDateTime} from "felles-ng2/støttefunksjoner/temporal";
import {Temporal} from "temporal-polyfill";
import {ZonedDateTimePeriode} from "felles-ng2/støttefunksjoner/zonedDateTimePerioder";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {Sakstidspunkt} from "felles-ng2/tjenester/Sakstidspunkter";
import {TemporalFormatering} from "felles-ng2/tjenester/TemporalFormatering";
import {Brukerkontekst} from "felles-ng2/tjenester/Brukerkontekst";
import PlainTime = Temporal.PlainTime;
import Duration = Temporal.Duration;
import ZonedDateTime = Temporal.ZonedDateTime;
import {AgendaelementModellDLM} from "felles-ng2/tjenester/datalager/SakDatatjeneste";

const debug = initDebugger('sakModellHjelpere');

/** Sorterer saker etter {@link AgendaelementModell#indeks} */
export function sorterSaker(saker: AgendaelementModell[]) {
    return saker.sort(sorterSak);
}

/** Sorterer saker etter {@link AgendaelementModell#indeks} */
export function sorterSak(a: AgendaelementModell, b: AgendaelementModell): number {
    return a.indeks - b.indeks;
}

/** Returnerer kopi av angitt klokkeslett i UTC-tid, angitt klokkeslett er forventet angitt i brukers tidssone.
 * Møtedato og dag må angis da denne benyttes for riktig håndtering av sommer/vintertid. */
export function startklokkeslettSakSomUTC(klokkeslettIBrukersTidssone: PlainTime | nil,
                                          dag: number | nil,
                                          møte: MøteMedDato,
                                          brukersTidssone: Tidssoneinformasjon): PlainTime | null {

    if (erNil(klokkeslettIBrukersTidssone)) return null;

    const referansedato = lesMøtedato(møte).start.add(Duration.from({days: dag ?? 0}));
    return referansedato
      .withTimeZone(brukersTidssone.somZoneId())
      .withPlainTime(klokkeslettIBrukersTidssone)
      .withTimeZone(Tidssoneinformasjon.UTC.somZoneId())
      .toPlainTime();
}

export function sakStarttidspunkt(sak: Pick<AgendaelementModell, 'starttidspunkt'>): ZonedDateTime | null {
    return lesZonedDateTimeSerialisertOpt(sak.starttidspunkt);
}

/**
 * Returnerer starttidspunkt fra angitt, eller møtedato.start + dag antall dager dersom starttidspunkt er nil.
 *
 * Sak.starttidspunkt kan være null fordi første sak på dag N>1 har blitt slettet,
 * ny første sak på dag N>1 vil da ikke ha starttidspunkt satt.
 * Når sak slettes skal normal hele sakslisten (møtet) relastes, men før dette skjer kan feks beregninger i
 * {@link Sakstidspunkter} lese sak.starttidspunkt og forvente at denne er ikke-null.
 */
export function sakStarttidspunktEllerMøtestartDersomNil(sak: Pick<AgendaelementModell, 'starttidspunkt'>, møtedato: ZonedDateTimePeriode, dag0Basert: number): ZonedDateTime {
    return sakStarttidspunkt(sak) ?? møtedato.start.add({days: dag0Basert})
}

export function sakVarighetSomDuration(sak: Pick<AgendaelementModell, 'varighet'>): Duration | null {
    return durationFraMinutter(sak.varighet);
}

/** Returnerer en tekstlig beskrivelse av sakens starttidspunkt og varighet */
export function sakStartOgVarighetBeskrivelse(sakstidspunkt: Sakstidspunkt, oversettelser: Oversettelser, gjeldendeTidssone: GjeldendeTidssone, temporalFormatering: TemporalFormatering): string {
    if (erIkkeNil(sakstidspunkt.start)) {
        const klokkeslett = plainTimeFraZonedDateTime(sakstidspunkt.start!, gjeldendeTidssone.brukersTidssone());
        if (sakstidspunkt.varighetAngitt)
            return oversettelser.oversett(i18n('Starter {start}, varer i {varighet}'), {start: temporalFormatering.formaterPlainTime(klokkeslett), varighet: temporalFormatering.formaterDuration(sakstidspunkt.varighet)});
        else if (sakstidspunkt.varighetBeregnet)
            return oversettelser.oversett(i18n('Starter {start}, varighet beregnet til {varighet} (varighet ikke angitt)'), {start: temporalFormatering.formaterPlainTime(klokkeslett), varighet: temporalFormatering.formaterDuration(sakstidspunkt.varighet)});
        else
            return oversettelser.oversett(i18n('Starter {start}, varighet er ikke angitt'), {start: temporalFormatering.formaterPlainTime(klokkeslett)});
    } else {
        return oversettelser.oversett(i18n('Start og varighet er ikke angitt'));
    }
}

export function harTilgangTilSaksdetaljer(sak: AgendaelementModell, kontekst: Brukerkontekst): boolean {
    if (sak.tilgang.erMoetePublisert) return true;
    if (kontekst.inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, sak.eiendeGruppeId) ||
      kontekst.inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.tilgangIkkePublisert, sak.eiendeGruppeId))
        return true;
    return false;
}

export function sakerDerBrukerErAnsvarlig(saker: AgendaelementModell[], kontekst: Brukerkontekst): AgendaelementModell[];
export function sakerDerBrukerErAnsvarlig(saker: AgendaelementModellDLM[], kontekst: Brukerkontekst): AgendaelementModellDLM[];
export function sakerDerBrukerErAnsvarlig(saker: AgendaelementModellDLM[], kontekst: Brukerkontekst): AgendaelementModellDLM[] {
    return saker.filter(sak => sak.ansvarlig?.brukerId == kontekst.bruker.id);
}
